import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  TextField,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import api from "../../utils/Url";
import CustomLabel from "../../CustomLable";
import { useFormik } from "formik";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { toast } from "react-toastify";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

export default function VehicleTravelled() {
  const { t } = useTranslation();

  const [VehicleNos, setVehicleNos] = useState<any[]>([]);
  const [Vehicle, setVehicle] = useState<string>("");
  const [vehicleDist, setVehicleDist] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isView, setView] = useState(false);
  const [hasData, setHasData] = useState(false);

  /* ---------------- FORM ---------------- */
  const formik = useFormik({
    initialValues: {
      fromDate: (() => {
        const d = new Date();
        d.setDate(d.getDate() - 7);
        return d.toISOString().split("T")[0];
      })(),
      toDate: new Date().toISOString().split("T")[0],
    },
    onSubmit: () => {},
  });

  /* ---------------- VEHICLE LIST ---------------- */
  useEffect(() => {
    fetchVehicleNos();
  }, []);

  const fetchVehicleNos = async () => {
    const res = await api.get("Rep/GetVehiclesm");

    const arr = res.data.data.map((x: any) => ({
      label: x.vehicleNo,
      value: x.vehicleNo,
      itemMasterid: x.itemMasterid,
    }));

    setVehicleNos(arr);
  };

  /* ---------------- VEHICLE TRAVEL DATA ---------------- */
  const getVehicleDistance = async () => {
    setIsLoading(true);
    setHasData(false);

    const payload = {
      itemMasterids: null,
      vehicleNo: Vehicle || null,
      fromDate: formik.values.fromDate,
      toDate: formik.values.toDate,
    };

    try {
      const res = await api.post("Rep/GetVehicleTravel", payload);

      const distanceArr = res?.data?.data?.distanceTravelled || [];
      const idleArr = res?.data?.data?.vehicleIdle || [];

      if (!distanceArr.length) {
        toast.error("No data found");
        setIsLoading(false);
        setVehicleDist(null);
        setHasData(false);
        return;
      }

      const combined = distanceArr.map((d: any) => {
        const idle = idleArr.find((i: any) => i.forDate === d.forDate);

        const date = d.forDate || d.tDate;
        const formattedDate = date
          ? new Date(date).toLocaleDateString("en-GB")
          : "__";

        return {
          trackDate: formattedDate,
          distance: d.distanceTravelled ?? 0,
          idle: idle?.idleTime ? idle.idleTime / 60 : 0,
        };
      });

      const labels = combined.map((x: any) => x.trackDate);
      const distanceData = combined.map((x: any) => x.distance);
      const idleData = combined.map((x: any) => Number(x.idle.toFixed(3)));

      setVehicleDist({
        labels,
        datasets: [
          {
            label: "Distance",
            data: distanceData,
            backgroundColor: "#66a3ff",
            borderWidth: 1,
          },
          {
            label: "Idle",
            data: idleData,
            backgroundColor: "#ff6666",
            borderWidth: 1,
            yAxisID: "y2",
          },
        ],
      });
      setHasData(true);
    } catch (error) {
      toast.error("Error fetching data");
      setVehicleDist(null);
      setHasData(false);
    } finally {
      setIsLoading(false);
    }
  };

  /* ---------------- UI ---------------- */
  return (
    <div>
      <Grid container spacing={2} justifyContent="center">
        <Grid xs={12} sm={3} md={3} item>
          <Autocomplete
            options={VehicleNos}
            onChange={(e, v) => setVehicle(v?.value || "")}
            fullWidth
            size="small"
            style={{ backgroundColor: "#fff" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={<CustomLabel text={t("text.SelectVehicleNo")} />}
              />
            )}
          />
        </Grid>

        <Grid xs={12} sm={3} md={3} item>
          <TextField
            type="date"
            name="fromDate"
            label={<CustomLabel text={t("text.DateFrom")} />}
            value={formik.values.fromDate}
            onChange={formik.handleChange}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            style={{ backgroundColor: "#fff" }}
          />
        </Grid>

        <Grid xs={12} sm={3} md={3} item>
          <TextField
            type="date"
            name="toDate"
            label={<CustomLabel text={t("text.DateTo")} />}
            value={formik.values.toDate}
            onChange={formik.handleChange}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
            style={{ backgroundColor: "#fff" }}
          />
        </Grid>

        <Grid xs={12} sm={2} md={2} item>
          <Button
            fullWidth
            startIcon={<VisibilityIcon />}
            disabled={!Vehicle}
            style={{
              backgroundColor: "var(--header-background)",
              color: "white",
            }}
            onClick={() => {
              getVehicleDistance();
              setView(true);
            }}
          >
            Show
          </Button>
        </Grid>

        {isView && (
          <Grid item xs={12} sx={{ mt: 3 }}>
            <div
              style={{
                border: "1px solid black",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
                minHeight: "450px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              {isLoading ? (
                <CircularProgress />
              ) : hasData && vehicleDist ? (
                <Bar
                  data={vehicleDist}
                  options={{
                    responsive: true,
                    scales: {
                      x: {
                        title: {
                          display: true,
                          text: "Track Date",
                        },
                      },
                      y: {
                        title: {
                          display: true,
                          text: "Distance (km)",
                        },
                        beginAtZero: true,
                      },
                      y2: {
                        title: {
                          display: true,
                          text: "Idle Time (mins)",
                        },
                        position: "right",
                        grid: {
                          drawOnChartArea: false,
                        },
                      },
                    },
                    plugins: {
                      tooltip: {
                        callbacks: {
                          label: function (ctx: any) {
                            return ctx.datasetIndex === 0
                              ? `${ctx.raw} km`
                              : `${ctx.raw} mins`;
                          },
                        },
                      },
                    },
                  }}
                  width={1000}
                  height={400}
                />
              ) : (
                <div style={{ textAlign: "center", color: "#666" }}>
                  No chart data available. Please select different criteria.
                </div>
              )}
            </div>
          </Grid>
        )}
      </Grid>
    </div>
  );
}


















// import React, { useEffect, useState } from "react";
// import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
// import axios from "axios";
// import { useNavigate } from "react-router-dom";
// import {
//   Card,
//   Grid,
//   CardContent,
//   Typography,
//   Divider,
//   Switch,
//   Button,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   TextField,
//   DialogActions,
//   Stack,
//   Paper,
//   CircularProgress,
//   Select,
//   MenuItem,
//   Radio,
//   FormLabel,
//   Autocomplete,
//   Checkbox,
//   ListItemText,
//   RadioGroup,
// } from "@mui/material";
// import { useTranslation } from "react-i18next";
// import api, { Node } from "../../utils/Url";
// import Box from "@mui/material/Box";

// import { toast } from "react-toastify";
// import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
// import CustomLabel from "../../CustomLable";
// import { styled } from "@mui/system";
// import { useFormik } from "formik";
// import VisibilityIcon from "@mui/icons-material/Visibility";
// import { BarChart } from "@mui/x-charts/BarChart";
// import { Bar } from "react-chartjs-2";
// import {
//   Chart as ChartJS,
//   CategoryScale,
//   LinearScale,
//   BarElement,
//   Title,
//   Tooltip,
//   Legend,
// } from "chart.js";
// import decryptData from "../../utils/crypto";

// ChartJS.register(
//   CategoryScale,
//   LinearScale,
//   BarElement,
//   Title,
//   Tooltip,
//   Legend
// );

// const containerStyle = {
//   width: "100%",
//   height: "500px",
// };

// interface MenuPermission {
//   isAdd: boolean;
//   isEdit: boolean;
//   isPrint: boolean;
//   isDel: boolean;
// }

// export default function VehicleTravelled() {
//   const { t } = useTranslation();

//   const [vNo, setVNo] = useState<any>("");
//   const navigate = useNavigate();
//   const [isLoading, setIsLoading] = useState(true);

//   const [isView, setView] = useState(false);
//   useEffect(() => {
//     getVehicleNo();
//   }, []);

//   const [VehicleNos, setVehicleNos] = useState<any>([
//     { value: "-1", label: t("text.VehicleNo") },
//   ]);

//   const [Vehicle, setVehicle] = useState<any>("");

//   const [vehicleDist, setVehicleDist] = useState<any>([]);

//   console.log("vehicleDist", vehicleDist);
//   const [vehicleIdle, setVehicleIdle] = useState([]);

//   const getVehicleNo = () => {
//     const collectData = {
//       orderby: "",
//       pageNo: 0,
//       pageSize: 0,
//       intnotnullvalue1: 0,
//       userId: "",

//       str1: "",
//       str2: "",
//       str3: "",
//       str4: "",
//       str5: "",
//       intvalue1: 0,
//       intvalue2: 0,
//       intvalue3: 0,
//       intvalue4: 0,
//       intnotnullvalue2: 0,
//       intnotnullvalue3: 0,
//       date1: new Date().toISOString(),
//       date2: new Date().toISOString(),
//       date3: new Date().toISOString(),
//       date4: new Date().toISOString(),
//       dec1: 0,
//       dec2: 0,
//       dec3: 0,
//       dec4: 0,
//       flag: true,
//       data: "",
//       success: true,
//       error: "",
//       selectPerindex: 0,
//       show: true,
//     };
//     api.post(`Dashboard/GetvVehicleNo`, collectData).then((res) => {
//       const arr = res?.data?.data.map((item: any) => ({
//         label: item.vehicleNo,
//         value: item.vehicleNo,
//       }));
//       setVehicleNos(arr);
//     });
//   };

//   const getVehicleDistance = () => {
//     const collectData = {
//       where: "@0=@0",
//       orderby: "",
//       pageNo: 0,
//       pageSize: 0,
//       intnotnullvalue1: 0,
//       userId: "string",
//       parameterValues: [
//         Vehicle,
//         formatDate(formik.values.fromDate),
//         formatDate(formik.values.toDate),
//       ],
//       str1: "",
//       str2: "",
//       str3: "",
//       str4: "",
//       str5: "",
//       intvalue1: 0,
//       intvalue2: 0,
//       intvalue3: 0,
//       intvalue4: 0,
//       intnotnullvalue2: 0,
//       intnotnullvalue3: 0,
//       date1: new Date().toISOString(),
//       date2: new Date().toISOString(),
//       date3: new Date().toISOString(),
//       date4: new Date().toISOString(),
//       dec1: 0,
//       dec2: 0,
//       dec3: 0,
//       dec4: 0,
//       flag: true,
//       data: "",
//       success: true,
//       error: "",
//       selectPerindex: 0,
//       show: true,
//     };
//     api
//       .post(`Dashboard/GetVehicleDistance`, collectData) // Replace collectData with your actual request data
//       .then((res) => {
//         const data = res?.data?.data;
//         setIsLoading(false);
//         console.log("data", data);

//         const combinedData = data?.listDistanceTravelled.map((item: any) => {
//           const matchingIdleData = data?.listVehicleIdle.find(
//             (idleItem: any) => idleItem.trackDate === item.trackDate
//           );

//           const formattedDate = new Date(item.trackDate).toLocaleDateString(
//             "en-GB"
//           );

//           return {
//             trackDate: formattedDate,
//             distance: item.distance,
//             secondsIdle: matchingIdleData ? matchingIdleData.secondsIdle : 0,
//           };
//         } ) ;

//         // const formattedData = combinedData?.map((item: any) => {
//         //   const formattedDate = new Date(item.trackDate).toLocaleDateString(
//         //     "en-GB"
//         //   );
//         //   return {
//         //     trackDate: formattedDate,
//         //     distance: item.distance,
//         //     secondsIdle: item.secondsIdle,
//         //   };
//         // });


      

//         const distanceData = (combinedData || []).map((item: any) => item.distance);
//         const idleData = (combinedData || []).map((item: any) => (item.secondsIdle/60).toFixed(3));
//         const trackDates = (combinedData || []).map((item: any) => item.trackDate);

//         console.log("idleData", idleData);

//         const series: any = [
//           { data: distanceData, stack: "A", label: "Distance" },
//           { data: idleData, stack: "B", label: "Idle" },
//         ];

//         setVehicleDist({
//           labels: trackDates,
//           datasets: [
//             {
//               data: distanceData,
//               label: "Distance",
//               backgroundColor: "#66a3ff",
//               borderColor: "rgba(75, 192, 192, 1)",
//               borderWidth: 1,
//             },
//             {
//               data: idleData,
//               label: "Idle",
//               backgroundColor: " #ff6666",
//               borderColor: "rgba(153, 102, 255, 1)",
//               borderWidth: 1,
//             },
//           ],
//         });


//         if (res?.data?.data === null) {
//           toast.error(res?.data?.message)
//         }
//       });
//   };

//   const formatDate = (date: any) => {
//     const d = new Date(date);
//     const day = String(d.getDate()).padStart(2, "0"); // Add leading zero if day < 10
//     const month = String(d.getMonth() + 1).padStart(2, "0"); // Add leading zero if month < 10
//     const year = d.getFullYear();
//     return `${day}-${month}-${year}`;
//   };

//   const formik = useFormik({
//     initialValues: {
//       genderID: -1,
//       fromDate: (() => {
//         const date = new Date();
//         date.setDate(date.getDate() - 7); // Subtract 7 days from current date
//         return date.toISOString().split("T")[0]; // Format the date in yyyy-mm-dd
//       })(),
//       toDate: new Date().toISOString().split("T")[0], // Current date in yyyy-mm-dd
//     },
//     onSubmit: async (values) => {
//       // Your submit logic here
//     },
//   });
//   return (
//     <div>
//       <Grid
//         container
//         spacing={2}
//         style={{
//           // backgroundColor:'red',
//          // marginTop: "2vh",
//          // marginTop: "2vh",
//           justifyContent: "center",
//         }}
//       >
//         <Grid xs={12} sm={3} md={3} item>
//           <Autocomplete
//             //multiple
//             disablePortal
//             id="combo-box-demo"
//             options={VehicleNos}
//             //getOptionLabel={(option: any) => option.label}
//             // value={
//             //     ZoneOption.find(
//             //         (option) => option.value === formik.values.fileTypeId
//             //     ) || null
//             // }
//             onChange={(event: any, newValue: any) => {
//               console.log(newValue?.value);
//               setVehicle(newValue?.value);
//             }}
//             fullWidth
//             size="small"
//             style={{ backgroundColor: "#ffff" }}
//             renderInput={(params: any) => (
//               <TextField
//                 {...params}
//                 label={<CustomLabel text={t("text.SelectVehicleNo")} />}
//               />
//             )}
//           />
//         </Grid>

//         <Grid xs={12} sm={3} md={3} item>
//           <TextField
//             type="date"
//             id="fromDate"
//             name="fromDate"
//             label={<CustomLabel text={t("text.DateFrom")} required={false} />}
//             value={formik.values.fromDate}
//             placeholder={t("text.DateFrom")}
//             style={{ backgroundColor: "#ffff" }}
//             size="small"
//             fullWidth
//             onChange={formik.handleChange}
//             onBlur={formik.handleBlur}
//             InputLabelProps={{ shrink: true }}
//           />
//         </Grid>

//         <Grid xs={12} sm={3} md={3} item>
//           <TextField
//             type="date"
//             id="toDate"
//             name="toDate"
//             label={<CustomLabel text={t("text.DateTo")} required={false} />}
//             value={formik.values.toDate}
//             placeholder={t("text.DateTo")}
//             style={{ backgroundColor: "#ffff" }}
//             size="small"
//             fullWidth
//             onChange={formik.handleChange}
//             onBlur={formik.handleBlur}
//             InputLabelProps={{ shrink: true }}
//           />
//         </Grid>

//         <Grid xs={12} sm={2} md={2} item>
//           <Button
//             type="submit"
//             fullWidth
//             startIcon={<VisibilityIcon />}
//             style={{
//               backgroundColor: `var(--header-background)`,
//               color: "white",
//             }}
//             onClick={() => {
//               getVehicleDistance();
//               setView(true);
//             }}
//           >
//             Show
//           </Button>
//         </Grid>

//         <Grid
//           container
//           spacing={2}
//           style={{
//             // backgroundColor:'red',
//             marginTop: "2vh",
//             justifyContent: "center",
//           }}
//         >
//           {isView && (
//             <Grid item xs={12} sm={12} lg={12}>
//               <div
//                 style={{
//                   border: "1px solid black",
//                   boxShadow: "0 4px 10px rgba(0, 0, 0, 0.2)",
//                 }}
//               >
//                 {isLoading ? (
//                   <div
//                     style={{
//                       display: "flex",
//                       justifyContent: "center",
//                       alignItems: "center",
//                     }}
//                   >
//                     <CircularProgress />
//                   </div>
//                 ) : (
//                   <Bar
//                     data={vehicleDist}
//                     options={{
//                       responsive: true,
//                       scales: {
//                         x: {
//                           title: {
//                             display: true,
//                             text: "Track Date",
//                           },
//                         },
//                         y: {
//                           title: {
//                             display: true,
//                             text: "Distance (km)",
//                           },
//                           beginAtZero: true,
//                         },
//                         y2: {
//                           title: {
//                             display: true,
//                             text: "Idle Time (mins)",
//                           },
//                           position: "right",
//                           ticks: {
//                             callback: function (value) {
//                               return value;
//                             },
//                           },
//                         },
//                       },
//                       plugins: {
//                         tooltip: {
//                           callbacks: {
//                             label: function (tooltipItem: any) {
//                               const datasetIndex = tooltipItem.datasetIndex;
//                               if (datasetIndex === 0) {
//                                 return tooltipItem.raw + "km";
//                               } else if (datasetIndex === 1) {
//                                 return tooltipItem.raw + " mins";
//                               }
//                             },
//                           },
//                         },
//                       },
//                     }}
//                     width={1000}
//                     height={400}
//                   />
//                 )}
//               </div>
//             </Grid>
//           )}
//         </Grid>
//       </Grid>
//          
//     </div>
//   );
// }
